/**
 * Iter 57 — Site-gate Kiosk for LABOUR attendance.
 *
 * Single device sits at site entry. Workers queue up, look at the camera,
 * server identifies them by face and auto-logs attendance.
 *
 * Authenticated supervisor sets project + IN/OUT mode once and leaves the
 * device running. Each capture POSTs to /api/mobile/attendance/kiosk/identify.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Check, X, LogIn, LogOut, Building2, Settings, AlertTriangle, Zap, Camera as CameraIcon, Search, UserPlus, UserCheck, RefreshCw, WifiOff } from 'lucide-react';
import FaceCaptureView from '../../components/FaceCaptureView';
import { getCurrentPosition, loadFaceModels } from '../../lib/faceRecognition';
import { captureAndDetect, isNativeFaceAvailable } from '../../lib/nativeFaceCapture';
import { useDeviceGuard, DesktopBlockScreen } from '../../components/DeviceGuard';
import { apiGet, apiPost } from '../../lib/api';

// Distinguish a real backend domain response (well-formed 4xx JSON with a
// `detail`) from a transport/server failure (no response at all, 5xx, or a
// non-JSON body like a Cloudflare error page) — the latter must NEVER be
// rendered to the kiosk operator as if it were a face-recognition result.
function isServerFailure(e) {
  const resp = e?.response;
  if (!resp) return true;                                    // network error / no response
  if (resp.status >= 500) return true;                        // 5xx from origin or edge
  const data = resp.data;
  if (typeof data === 'string') return true;                  // HTML/Cloudflare error page, not JSON
  if (data && typeof data === 'object' && data.detail === undefined) return true; // malformed JSON
  return false;
}

const serverErrorResult = () => ({ matched: false, serverError: true, error: 'Server is unreachable or busy — please try again in a moment', ts: new Date() });

export default function KioskAttendance() {
  const navigate = useNavigate();
  const deviceAllowed = useDeviceGuard('kiosk');
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(localStorage.getItem('kiosk.project_id') || '');
  // "Auto" punch direction removed. It was causing rapid double-
  // punches (a worker would Auto-IN and then immediately Auto-OUT on the next
  // capture within seconds). Operators must now explicitly choose IN or OUT
  // for the shift. Migrate any pre-existing 'auto' value to 'in'.
  const [kind, setKind] = useState(() => {
    const stored = localStorage.getItem('kiosk.kind');
    return (stored === 'auto' || !stored) ? 'in' : stored;
  });
  const [setupOpen, setSetupOpen] = useState(true);
  const [gps, setGps] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);   // the immediate green/red card after a scan
  const [summary, setSummary] = useState({ present_today: 0, present_yesterday: 0, recent_punches: [] });
  // Iter 209 — legacy worker has no subcontractor: prompt the operator to link
  // an approved one in-flow, then re-mark attendance automatically.
  const [subPrompt, setSubPrompt] = useState(null);     // { labour_id, labour_name, can_submit_new }
  const [subcontractors, setSubcontractors] = useState([]);
  const [chosenSubId, setChosenSubId] = useState('');
  const [linkingSub, setLinkingSub] = useState(false);
  const [newSubName, setNewSubName] = useState('');      // inline "add new subcontractor"
  const [submittingSub, setSubmittingSub] = useState(false);
  const [pendingList, setPendingList] = useState([]);    // held attendance awaiting subcontractor approval
  const pendingCaptureRef = useRef(null);               // capture args to retry after linking
  const pendingRetryRef = useRef(null);                 // retry fn (works for both JS-v1 and native-v2)
  // Fast pipeline (native ML Kit + backend InsightFace). On web this stays false
  // and the kiosk uses the existing JS faceapi.js pipeline (FaceCaptureView).
  const [useNativePipeline, setUseNativePipeline] = useState(false);
  const [nativeBusy, setNativeBusy] = useState(false);
  const [nativeHint, setNativeHint] = useState('Tap to identify the worker');
  const [perfCache, setPerfCache] = useState({});       // labour_id -> { recent_scores, fetchedAt }
  const [now, setNow] = useState(() => new Date());  // ticking clock for the desktop header
  const lastTickRef = useRef(0);
  // Session throughput — how many workers this operator has processed this sitting.
  const [session, setSession] = useState(() => ({ count: 0, startedAt: Date.now(), totalMs: 0, lastMs: null }));
  const bumpSession = (ms) => setSession((s) => ({
    count: s.count + 1,
    startedAt: s.startedAt,
    totalMs: s.totalMs + (ms || 0),
    lastMs: ms ?? s.lastMs,
  }));
  // Re-enrol alert: shown when 3+ consecutive scans are unrecognised at this kiosk.
  const [reenrollAlert, setReenrollAlert] = useState(null); // { consecutiveFails, ts }
  // Inline labour search state for the reenroll flow
  const [reenrollSearch, setReenrollSearch] = useState('');
  const [reenrollResults, setReenrollResults] = useState(null); // null = not searched yet
  const [reenrollSearching, setReenrollSearching] = useState(false);

  // Tick the clock once a second (desktop kiosk display)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    apiGet('/projects').then((r) => {
      const list = Array.isArray(r) ? r : (r?.projects || []);
      setProjects(list);
      if (!projectId && list.length) setProjectId(list[0].id);
    }).catch(() => {});
    // Establish GPS once on mount
    getCurrentPosition().then(setGps);
    // Pre-warm the face models into JS memory before the first tap (cuts the
    // cold-load 2-3s off the first identification).
    loadFaceModels().catch(() => {});
    // Detect the fast native pipeline (ML Kit + InsightFace). Web → false.
    isNativeFaceAvailable().then((available) => {
      setUseNativePipeline(available);
      console.log(`Face pipeline: ${available ? 'native ML Kit + InsightFace' : 'JS fallback (faceapi.js)'}`);
    }).catch(() => setUseNativePipeline(false));
  }, []); // eslint-disable-line

  useEffect(() => { if (projectId) localStorage.setItem('kiosk.project_id', projectId); }, [projectId]);
  useEffect(() => { localStorage.setItem('kiosk.kind', kind); }, [kind]);

  // Approved subcontractors (for the legacy-worker link prompt).
  useEffect(() => {
    apiGet('/labour/subcontractors').then((r) => setSubcontractors(r?.items || [])).catch(() => {});
  }, []);

  // Today's attendance summary (present today / yesterday + recent punch-ins).
  const fetchSummary = async () => {
    if (!projectId) return;
    try {
      const r = await apiGet(`/mobile/attendance/kiosk/today-summary?project_id=${encodeURIComponent(projectId)}`);
      if (r) setSummary(r);
    } catch { /* non-fatal — leave previous summary */ }
  };
  useEffect(() => { fetchSummary(); }, [projectId]); // eslint-disable-line

  // Held attendance awaiting subcontractor approval (shown distinctly below).
  const fetchPending = async () => {
    if (!projectId) return;
    try {
      const r = await apiGet(`/labour/kiosk-pending-attendance?project_id=${encodeURIComponent(projectId)}`);
      setPendingList(r?.items || []);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { fetchPending(); }, [projectId]); // eslint-disable-line

  // Step 15 — fetch a worker's recent performance (cached 10 min) for the dots.
  const fetchPerf = async (labourId) => {
    if (!labourId) return;
    const cached = perfCache[labourId];
    if (cached && (Date.now() - cached.fetchedAt) < 10 * 60 * 1000) return;
    try {
      const r = await apiGet(`/labour/${labourId}/performance-summary?days=7`);
      setPerfCache((c) => ({ ...c, [labourId]: { recent_scores: r?.recent_scores || [], fetchedAt: Date.now() } }));
    } catch { /* non-fatal */ }
  };

  const runIdentify = async ({ descriptor, photoDataUrl, liveness, captureMs }) => {
    setBusy(true);
    try {
      const r = await apiPost('/mobile/attendance/kiosk/identify-v2', {
        descriptor,
        photo: photoDataUrl,          // identify-v2 requires 'photo', not 'photo_url'
        lat: gps?.lat,
        lng: gps?.lng,
        accuracy_m: gps?.accuracy,
        project_id: projectId,
        kind,
        device_id: 'kiosk',
        liveness_signals: liveness || undefined,
      }, { timeout: 10000 });
      setLastResult({ ...r, photoDataUrl, ts: new Date() });
      if (r.matched) {
        if (navigator.vibrate) navigator.vibrate(100);
        bumpSession(captureMs);
        fetchSummary();
        fetchPerf(r.entity_id);
        setReenrollAlert(null); // reset alert on success
      } else if (r.suggest_reenroll) {
        setReenrollAlert({ consecutiveFails: r.consecutive_fails, ts: new Date() }); setReenrollSearch(""); setReenrollResults(null);
      }
    } catch (e) {
      if (isServerFailure(e)) {
        setLastResult(serverErrorResult());
        return;
      }
      const detail = e?.response?.data?.detail;
      // Step 3 — legacy worker with no subcontractor: prompt, don't show red card.
      if (detail && typeof detail === 'object' && detail.code === 'no_subcontractor') {
        pendingCaptureRef.current = { descriptor, photoDataUrl, liveness, captureMs };
        setChosenSubId('');
        setNewSubName('');
        setSubPrompt({ labour_id: detail.labour_id, labour_name: detail.labour_name, can_submit_new: detail.can_submit_new !== false });
        setLastResult(null);
      } else {
        setLastResult({ matched: false, error: (typeof detail === 'string' ? detail : detail?.message) || 'Match failed', ts: new Date() });
      }
    } finally { setBusy(false); }
  };

  const onCapture = async (args) => {
    // Soft debounce — ignore captures fired within 1.5s of the last one
    const now = Date.now();
    if (now - lastTickRef.current < 1500) return;
    lastTickRef.current = now;
    await runIdentify(args);
  };

  // ─── Fast pipeline (native ML Kit + backend InsightFace) ───
  // Sends a captured photo + ML Kit liveness signals to identify-v2. Reused as
  // the retry after a subcontractor is linked. AbortController caps the call at 10s.
  const sendV2 = async (capture) => {
    setBusy(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const r = await apiPost('/mobile/attendance/kiosk/identify-v2', {
        photo: capture.photo_base64,
        project_id: projectId,
        kind,
        lat: gps?.lat,
        lng: gps?.lng,
        accuracy_m: gps?.accuracy,
        bbox: capture.bbox || undefined,
        liveness_passed: capture.liveness_passed,
        liveness_attributes: capture.liveness_attributes,
        device_id: 'kiosk',
      }, { timeout: 10000, signal: controller.signal });

      // No worker on this project is enrolled for fast recognition yet → use JS.
      if (r.fallback) {
        toast.info(r.message || 'Switching to standard scan');
        setUseNativePipeline(false);
        return;
      }
      const photoDataUrl = `data:image/jpeg;base64,${capture.photo_base64}`;
      setLastResult({ ...r, photoDataUrl, ts: new Date() });
      if (r.matched) {
        if (navigator.vibrate) navigator.vibrate(100);
        bumpSession(r.recognition_ms);
        fetchSummary();
        fetchPerf(r.entity_id);
        setReenrollAlert(null); // reset alert on success
      } else if (r.suggest_reenroll) {
        setReenrollAlert({ consecutiveFails: r.consecutive_fails, ts: new Date() }); setReenrollSearch(""); setReenrollResults(null);
      }
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') {
        setLastResult({ matched: false, error: 'Match timed out — please rescan', ts: new Date() });
      } else if (isServerFailure(e)) {
        setLastResult(serverErrorResult());
      } else {
        const detail = e?.response?.data?.detail;
        if (detail && typeof detail === 'object' && detail.code === 'no_subcontractor') {
          pendingRetryRef.current = () => sendV2(capture);
          setChosenSubId('');
          setNewSubName('');
          setSubPrompt({ labour_id: detail.labour_id, labour_name: detail.labour_name, can_submit_new: false });
          setLastResult(null);
        } else if (detail && typeof detail === 'object' && detail.code === 'liveness_failed') {
          setNativeHint(detail.message || 'Liveness check failed — please try again');
          setLastResult({ matched: false, error: detail.message || 'Liveness check failed', ts: new Date() });
        } else {
          setLastResult({ matched: false, error: (typeof detail === 'string' ? detail : detail?.message) || 'Match failed', ts: new Date() });
        }
      }
    } finally {
      clearTimeout(timeout);
      setBusy(false);
    }
  };

  const runNativeIdentify = async () => {
    const now = Date.now();
    if (now - lastTickRef.current < 1500) return;
    lastTickRef.current = now;
    setNativeBusy(true);
    setNativeHint('Opening camera…');
    try {
      const capture = await captureAndDetect();
      if (!capture.success) {
        setNativeHint(capture.reason === 'cancelled' ? 'Cancelled — tap to retry' : 'No face detected — please try again');
        return;
      }
      if (!capture.liveness_passed) {
        const a = capture.liveness_attributes || {};
        if ((a.left_eye_open ?? 1) < 0.5 || (a.right_eye_open ?? 1) < 0.5) setNativeHint('Please open your eyes fully');
        else if (Math.abs(a.head_yaw ?? 0) > 25) setNativeHint('Please face the camera directly');
        else setNativeHint('Please hold your head straight');
        return;
      }
      setNativeHint('Identifying…');
      await sendV2(capture);
      setNativeHint('Tap to identify the next worker');
    } catch {
      setNativeHint('Error — please try again');
    } finally {
      setNativeBusy(false);
    }
  };

  const confirmSubcontractor = async () => {
    if (!chosenSubId || !subPrompt) { toast.error('Pick a subcontractor'); return; }
    setLinkingSub(true);
    try {
      await apiPost(`/labour/${subPrompt.labour_id}/set-subcontractor`, { subcontractor_id: chosenSubId });
      toast.success(`Subcontractor linked for ${subPrompt.labour_name}`);
      const retry = pendingCaptureRef.current;
      setSubPrompt(null);
      pendingCaptureRef.current = null;
      if (pendingRetryRef.current) {
        const fn = pendingRetryRef.current;
        pendingRetryRef.current = null;
        await fn();   // native-v2 retry
      } else if (retry) {
        await runIdentify(retry);   // re-mark attendance automatically (JS-v1)
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (msg?.message || 'Could not link subcontractor'));
    } finally { setLinkingSub(false); }
  };

  // OPTION B — add a brand-new subcontractor inline and record attendance immediately.
  const submitNewSubcontractor = async () => {
    if (newSubName.trim().length < 3 || !subPrompt) { toast.error('Enter the full subcontractor / firm name (at least 3 characters)'); return; }
    const cap = pendingCaptureRef.current || {};
    setSubmittingSub(true);
    try {
      const r = await apiPost('/labour/subcontractors/kiosk-submit', {
        name: newSubName.trim(),
        labour_id: subPrompt.labour_id,
        project_id: projectId,
        kind,
        descriptor: cap.descriptor,
        photo_url: cap.photoDataUrl,
      });
      const labourName = subPrompt.labour_name;
      setSubPrompt(null);
      pendingCaptureRef.current = null;
      setNewSubName('');
      setLastResult({
        matched: true,
        inline: true,
        confirmed: true,
        pending: false,
        entity_name: labourName,
        photoDataUrl: cap.photoDataUrl,
        message: r.message || 'Attendance recorded',
        ts: new Date(),
      });
      if (navigator.vibrate) navigator.vibrate(100);
      bumpSession(cap.captureMs);
      toast.success(r.message || 'Attendance recorded');
      fetchSummary();
      fetchPending();
      apiGet('/labour/subcontractors').then((rr) => setSubcontractors(rr?.items || [])).catch(() => {});
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (msg?.message || 'Could not submit subcontractor'));
    } finally { setSubmittingSub(false); }
  };

  const projectName = projects.find((p) => p.id === projectId)?.name || '—';

  // Labour search for the reenroll flow
  const searchLabourForReenroll = async () => {
    if (!reenrollSearch.trim()) return;
    setReenrollSearching(true);
    try {
      const r = await apiGet('/labour', { q: reenrollSearch.trim(), limit: 8 });
      setReenrollResults(r.items || []);
    } catch { setReenrollResults([]); }
    finally { setReenrollSearching(false); }
  };

  // Throughput derived from the ticking `now` so the per-minute rate stays live.
  const sessionMins = Math.max((now.getTime() - session.startedAt) / 60000, 1 / 60);
  const perMin = session.count > 0 ? session.count / sessionMins : 0;
  const avgSec = session.count > 0 ? session.totalMs / session.count / 1000 : 0;

  // Field-only gate: block desktops/laptops entirely (no camera init).
  if (deviceAllowed === null) return null;
  if (deviceAllowed === false) return <DesktopBlockScreen />;

  if (setupOpen) {
    return (
      <div className="min-h-screen bg-[#0f1a2e] text-white p-6 flex flex-col items-center justify-center">
        <div className="w-full max-w-md md:max-w-xl lg:max-w-2xl space-y-5">
          <header className="text-center mb-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-display font-bold">Site Gate Kiosk</div>
            <div className="font-display font-bold text-3xl md:text-4xl lg:text-5xl mt-1">Setup</div>
          </header>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1">Project</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
              className="w-full bg-white/10 border border-white/15 rounded-xl px-4 py-3 text-base font-display focus:outline-none focus:border-white/40"
              data-testid="kiosk-project-picker">
              <option value="" className="text-slate-900">Select project…</option>
              {projects.map((p) => <option key={p.id} value={p.id} className="text-slate-900">{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold block mb-1">Punch direction</label>
            <div className="grid grid-cols-2 gap-2">
              <KindButton active={kind === 'in'} onClick={() => setKind('in')} icon={LogIn} label="Punch In" testid="kiosk-kind-in" />
              <KindButton active={kind === 'out'} onClick={() => setKind('out')} icon={LogOut} label="Punch Out" testid="kiosk-kind-out" />
            </div>
            <p className="text-[10px] text-white/40 mt-2">
              Set this for the entire shift at this gate. Switch to <strong>Punch Out</strong> when the shift ends so the kiosk stops marking IN.
              Auto-direction was removed to prevent rapid double-punches.
            </p>
          </div>
          <button
            disabled={!projectId}
            onClick={() => { if (!projectId) return; setSetupOpen(false); }}
            className="w-full bg-white text-slate-900 py-4 rounded-full font-display font-bold disabled:opacity-40"
            data-testid="kiosk-start-btn"
          >Start kiosk →</button>
          <button onClick={() => navigate('/app')} className="w-full text-center text-xs text-white/40 py-2">Back to ERP</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f1a2e] via-[#1e2a4d] to-[#0f1a2e] text-white">
      {/* Top status bar */}
      <header className="px-5 md:px-10 pt-6 pb-3 flex items-center justify-between max-w-[1600px] mx-auto w-full">
        <div className="flex items-center gap-2 text-xs md:text-sm">
          <Building2 size={14} className="text-white/50 md:hidden" />
          <Building2 size={20} className="text-white/50 hidden md:block" />
          <span className="font-display font-bold truncate max-w-[200px] md:max-w-[400px] md:text-base lg:text-lg">{projectName}</span>
          <span className={`text-[10px] md:text-xs uppercase tracking-wider px-2 py-0.5 rounded-full ${
            kind === 'in' ? 'bg-emerald-500/20 text-emerald-300'
            : 'bg-rose-500/20 text-rose-300'
          }`}>{kind === 'in' ? 'Punch In' : 'Punch Out'}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:block text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Local time</div>
            <div className="font-display font-bold text-lg lg:text-xl tabular-nums" data-testid="kiosk-clock">
              {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
          </div>
          <button onClick={() => setSetupOpen(true)} className="text-white/50 hover:text-white p-2" data-testid="kiosk-settings-btn">
            <Settings size={18} />
          </button>
        </div>
      </header>

      <main className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 px-5 md:px-10 pb-10 max-w-[1600px] mx-auto w-full md:items-start">
        {/* Camera — bigger on desktop */}
        <section className="md:sticky md:top-4">
          <div className="md:rounded-3xl md:overflow-hidden md:shadow-2xl md:shadow-black/30">
            {useNativePipeline ? (
              <div className="bg-black/40 rounded-2xl p-6 md:p-10 flex flex-col items-center justify-center text-center min-h-[320px] md:min-h-[420px]" data-testid="kiosk-native-capture">
                <div className="inline-flex items-center gap-1.5 bg-white/10 text-white/70 text-[10px] uppercase tracking-widest font-display font-bold px-3 py-1 rounded-full mb-6">
                  <Zap size={12} /> Fast recognition
                </div>
                <button
                  type="button"
                  onClick={runNativeIdentify}
                  disabled={nativeBusy || busy}
                  className="w-40 h-40 md:w-52 md:h-52 rounded-full bg-white text-slate-900 flex flex-col items-center justify-center gap-2 active:scale-[0.97] disabled:opacity-50 shadow-2xl transition-transform"
                  data-testid="kiosk-native-identify-btn"
                >
                  <CameraIcon size={44} />
                  <span className="font-display font-bold text-sm uppercase tracking-wider">
                    {nativeBusy ? 'Working…' : busy ? 'Identifying…' : 'Tap to identify'}
                  </span>
                </button>
                <p className="mt-6 text-sm md:text-base text-white/70" data-testid="kiosk-native-hint">{nativeHint}</p>
              </div>
            ) : (
              <FaceCaptureView liveness onCapture={onCapture} busy={busy} captureLabel="Identify" facingMode="user" helpText="Worker: face the camera and tap" />
            )}
          </div>
          <p className="mt-2 text-center text-[11px] md:text-xs text-white/40">
            GPS: {gps ? `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)} (±${Math.round(gps.accuracy)}m)` : 'fetching…'}
          </p>
        </section>

        {/* Right column — immediate result + today's attendance */}
        <section>
          {/* Session throughput — operator productivity at a glance */}
          <div className="mb-5 md:mb-6 grid grid-cols-3 gap-2 md:gap-3" data-testid="kiosk-throughput">
            <ThroughStat label="Scanned" value={session.count} testid="kiosk-throughput-count" />
            <ThroughStat label="Avg time" value={session.count ? `${avgSec.toFixed(1)}s` : '—'} testid="kiosk-throughput-avg" />
            <ThroughStat label="Per min" value={session.count ? perMin.toFixed(1) : '—'} testid="kiosk-throughput-rate" />
          </div>

          {/* ─── Re-enrol flow: shown after 3+ consecutive unrecognised scans ─── */}
          {reenrollAlert && !subPrompt && (
            <div className="mb-5 md:mb-6 bg-rose-500/10 border border-rose-400/40 rounded-2xl overflow-hidden" data-testid="kiosk-reenroll-alert">
              {/* Header */}
              <div className="flex items-start gap-2 p-4 pb-3">
                <AlertTriangle size={18} className="text-rose-300 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm md:text-base text-rose-100 font-display font-bold">
                    {reenrollAlert.consecutiveFails} scans not recognised
                  </div>
                  <div className="text-xs text-rose-200/70 mt-0.5">
                    Is this worker already registered or new to the site?
                  </div>
                </div>
                <button
                  onClick={() => { setReenrollAlert(null); setReenrollSearch(''); setReenrollResults(null); }}
                  className="text-white/30 hover:text-white/60 p-1"
                  data-testid="kiosk-reenroll-dismiss"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Option A — already registered: inline search */}
              <div className="border-t border-rose-400/20 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-2 flex items-center gap-1.5">
                  <UserCheck size={10} /> Already Registered — Re-enrol Face
                </div>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={reenrollSearch}
                    onChange={(e) => setReenrollSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchLabourForReenroll()}
                    placeholder="Name / Mobile / Aadhar…"
                    className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-rose-300"
                    data-testid="kiosk-reenroll-search-input"
                  />
                  <button
                    onClick={searchLabourForReenroll}
                    disabled={reenrollSearching || !reenrollSearch.trim()}
                    className="bg-rose-500 hover:bg-rose-400 disabled:opacity-40 text-white rounded-lg px-3 py-2"
                    data-testid="kiosk-reenroll-search-btn"
                  >
                    {reenrollSearching ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
                  </button>
                </div>

                {/* Search results */}
                {reenrollResults !== null && (
                  <div className="space-y-1" data-testid="kiosk-reenroll-results">
                    {reenrollResults.length === 0 ? (
                      <div className="text-xs text-white/50 py-1 text-center">No matching labour found</div>
                    ) : (
                      reenrollResults.map((w) => (
                        <button
                          key={w.id}
                          onClick={() => {
                            setReenrollAlert(null);
                            setReenrollSearch('');
                            setReenrollResults(null);
                            navigate(`/m/labour/enroll?labour_id=${w.id}&back=/m/labour`);
                          }}
                          className="w-full flex items-center justify-between bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg px-3 py-2 text-left active:scale-[0.98] transition"
                          data-testid={`kiosk-reenroll-pick-${w.id}`}
                        >
                          <div>
                            <div className="text-xs font-display font-bold text-white">{w.name}</div>
                            <div className="text-[10px] text-white/50">
                              {w.trade || '—'} · {w.subcontractor_name || 'No contractor'} ·{' '}
                              {w.face_enrolled
                                ? <span className="text-amber-300">Re-enrol</span>
                                : <span className="text-emerald-300">Enrol face</span>}
                            </div>
                          </div>
                          <Check size={14} className="text-white/40" />
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Option B — new worker: go to registration */}
              <div className="border-t border-rose-400/20 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-2 flex items-center gap-1.5">
                  <UserPlus size={10} /> New Worker — Register First
                </div>
                <button
                  onClick={() => {
                    setReenrollAlert(null);
                    navigate('/m/labour-register');
                  }}
                  className="w-full bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl px-4 py-2.5 text-xs font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2"
                  data-testid="kiosk-reenroll-new-labour-btn"
                >
                  <UserPlus size={13} /> Add New Labour Registration
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — legacy worker has no subcontractor on record (amber, distinct from "not recognised") */}
          {subPrompt && (            <div className="mb-5 md:mb-6 bg-amber-500/15 border border-amber-400/40 rounded-2xl p-4 md:p-5" data-testid="kiosk-subcontractor-prompt">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={18} className="text-amber-300 shrink-0 mt-0.5" />
                <div className="text-sm md:text-base text-amber-100 font-display font-bold leading-snug">
                  {subPrompt.labour_name} has no subcontractor on record.
                </div>
              </div>

              {/* OPTION A — select an existing approved subcontractor */}
              <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-1">Option A · Select approved</div>
              <select
                value={chosenSubId}
                onChange={(e) => setChosenSubId(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/50 mb-2"
                data-testid="kiosk-subcontractor-select"
              >
                <option value="" className="text-slate-900">— Select approved subcontractor —</option>
                {subcontractors.filter((s) => (s.status || 'approved') === 'approved').map((s) => (
                  <option key={s.id} value={s.id} className="text-slate-900">{s.name}</option>
                ))}
              </select>
              <button
                onClick={confirmSubcontractor}
                disabled={linkingSub || !chosenSubId}
                className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30 rounded-xl px-4 py-2.5 text-sm font-display font-bold uppercase tracking-wider disabled:opacity-50 mb-4"
                data-testid="kiosk-subcontractor-confirm"
              >
                {linkingSub ? 'Linking…' : 'Confirm & Mark Attendance'}
              </button>

              {/* OPTION B — add a brand-new subcontractor (held pending approval) */}
              {subPrompt.can_submit_new && (
                <>
                  <div className="flex items-center gap-2 my-2 text-[10px] uppercase tracking-widest text-white/40 font-bold">
                    <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-1">Option B · Add new</div>
                  <input
                    value={newSubName}
                    onChange={(e) => setNewSubName(e.target.value)}
                    placeholder="Full subcontractor / firm name"
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-white/50 mb-1.5"
                    data-testid="kiosk-new-subcontractor-input"
                  />
                  <p className="text-[10px] text-white/50 mb-2">Enter the full name. Attendance is marked now; Procurement completes vendor registration before any invoice is passed.</p>
                  <button
                    onClick={submitNewSubcontractor}
                    disabled={submittingSub || newSubName.trim().length < 3}
                    className="w-full bg-white/10 hover:bg-white/20 border border-white/30 text-white rounded-xl px-4 py-2.5 text-sm font-display font-bold uppercase tracking-wider disabled:opacity-50"
                    data-testid="kiosk-new-subcontractor-submit"
                  >
                    {submittingSub ? 'Submitting…' : 'Add contractor & mark attendance'}
                  </button>
                </>
              )}

              <button
                onClick={() => { setSubPrompt(null); pendingCaptureRef.current = null; setNewSubName(''); }}
                className="w-full mt-3 px-4 py-2 text-xs font-display font-bold uppercase tracking-wider text-white/50 border border-white/20 rounded-xl"
                data-testid="kiosk-subcontractor-cancel"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Immediate match result (green/red) — appears right after a scan */}
          {lastResult && (
            <div className="mb-5 md:mb-6" data-testid="kiosk-last-result">
              <div className="text-[10px] md:text-xs uppercase tracking-widest text-white/50 font-bold mb-2">Last scan</div>
              <div className={`flex items-center gap-3 md:gap-5 rounded-2xl p-3 md:p-5 ${lastResult.serverError ? 'bg-amber-500/15 border border-amber-400/30' : !lastResult.matched ? 'bg-rose-500/15 border border-rose-400/30' : lastResult.pending ? 'bg-amber-500/15 border border-amber-400/40' : lastResult.low_confidence ? 'bg-amber-500/15 border border-amber-400/40' : 'bg-emerald-500/15 border border-emerald-400/30'}`}>
                {lastResult.photoDataUrl ? (
                  <img src={lastResult.photoDataUrl} alt="" className="w-12 h-12 md:w-20 md:h-20 rounded-xl object-cover" />
                ) : (
                  <div className="w-12 h-12 md:w-20 md:h-20 rounded-xl bg-black/30 flex items-center justify-center">
                    {lastResult.serverError ? <WifiOff size={20} className="text-amber-300" /> : <X size={20} className="text-rose-300" />}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {lastResult.inline ? (
                    <>
                      <div className="font-display font-bold text-sm md:text-xl truncate" data-testid="kiosk-pending-result">{lastResult.entity_name}</div>
                      <div className={`mt-1 inline-flex items-center gap-1 text-[10px] md:text-sm font-display font-bold rounded-md px-2 py-0.5 text-emerald-200 bg-emerald-500/20 border border-emerald-400/40`}>
                        ✓ Attendance recorded
                      </div>
                    </>
                  ) : lastResult.matched ? (
                    <>
                      <div className="flex items-center gap-2">
                        <div className="font-display font-bold text-sm md:text-xl truncate">{lastResult.entity_name}</div>
                        <span className={`shrink-0 font-display font-bold tabular-nums text-sm md:text-2xl ${lastResult.low_confidence ? 'text-amber-300' : 'text-emerald-300'}`} data-testid="kiosk-match-score">
                          {lastResult.confidence_pct ?? Math.round((1 - lastResult.match_score) * 100)}%
                        </span>
                      </div>
                      <div className="text-[10px] md:text-sm text-white/60 uppercase tracking-wider">
                        {lastResult.kind}{lastResult.second_best_score != null ? ` · 2nd-best ${Math.round((1 - lastResult.second_best_score) * 100)}%` : ''}
                      </div>
                      {lastResult.subcontractor_name && (
                        <div className="text-[10px] md:text-xs text-white/50 mt-0.5 truncate" data-testid="kiosk-match-subcontractor">
                          {lastResult.subcontractor_name}
                        </div>
                      )}
                      {lastResult.low_confidence && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] md:text-sm font-display font-bold text-amber-200 bg-amber-500/20 border border-amber-400/40 rounded-md px-2 py-0.5" data-testid="kiosk-lowconf-warning">
                          <AlertTriangle size={12} /> Low confidence — verify worker identity
                        </div>
                      )}
                      {/* On-site badge + IN/OUT times */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {lastResult.is_onsite ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold" data-testid="kiosk-onsite-badge">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            ON-SITE NOW
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-white/70 text-xs font-semibold" data-testid="kiosk-checkedout-badge">
                            Checked Out
                          </span>
                        )}
                        {lastResult.check_in_time && (
                          <span className="text-xs text-white/70">
                            IN: <strong className="text-emerald-300">
                              {new Date(lastResult.check_in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                            </strong>
                          </span>
                        )}
                        {lastResult.check_out_time && (
                          <span className="text-xs text-white/70">
                            OUT: <strong className="text-white/90">
                              {new Date(lastResult.check_out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                            </strong>
                          </span>
                        )}
                      </div>
                    </>
                  ) : lastResult.serverError ? (
                    <>
                      <div className="font-display font-bold text-sm md:text-xl" data-testid="kiosk-server-error">Connection problem</div>
                      <div className="text-[10px] md:text-sm text-white/60">{lastResult.error}</div>
                    </>
                  ) : (
                    <>
                      <div className="font-display font-bold text-sm md:text-xl">{lastResult.reason === 'ambiguous_match' ? 'Ambiguous — rescan' : 'Not recognised'}</div>
                      <div className="text-[10px] md:text-sm text-white/60">{lastResult.error || lastResult.message || (lastResult.best_score ? `Best score ${lastResult.best_score}` : 'Unknown worker')}</div>
                    </>
                  )}
                </div>
                {lastResult.matched ? <Check size={20} className={`md:w-8 md:h-8 ${lastResult.pending || lastResult.low_confidence ? 'text-amber-300' : 'text-emerald-300'}`} /> : lastResult.serverError ? <WifiOff size={20} className="text-amber-300 md:w-8 md:h-8" /> : <X size={20} className="text-rose-300 md:w-8 md:h-8" />}
              </div>
            </div>
          )}

          {/* Today's attendance — operationally useful roster (successful punches only) */}
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-[10px] md:text-xs uppercase tracking-widest text-white/50 font-bold">Today&apos;s attendance</div>
          </div>
          <div className="text-[11px] md:text-sm text-white/50 mb-3 md:mb-4" data-testid="kiosk-today-subtitle">
            <span className="text-emerald-300 font-display font-bold tabular-nums">{summary.present_today}</span> worker{summary.present_today === 1 ? '' : 's'} present today
            <span className="text-white/30"> · </span>
            <span className="tabular-nums">{summary.present_yesterday}</span> yesterday
          </div>
          <ul className="space-y-2 md:space-y-3" data-testid="kiosk-today-list">
            {pendingList.map((p) => (
              <li key={p.id} className="flex items-center gap-3 md:gap-5 rounded-2xl p-3 md:p-4 bg-amber-500/10 border border-amber-400/30" data-testid="kiosk-pending-item">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0 text-lg">⏳</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm md:text-lg truncate">{p.labour_name || '—'}</div>
                  <div className="text-[10px] md:text-sm text-amber-200/80 uppercase tracking-wider truncate">
                    Pending approval · {p.subcontractor_name || 'new subcontractor'}
                  </div>
                </div>
              </li>
            ))}
            {(summary.recent_punches || []).length === 0 && pendingList.length === 0 && (
              <li className="text-sm md:text-base text-white/40 italic p-6 md:p-10 text-center border border-dashed border-white/15 rounded-2xl">
                No punch-ins yet today.
              </li>
            )}
            {(summary.recent_punches || []).map((p, i) => (
              <li key={`${p.labour_name}-${p.punch_time}-${i}`} className="flex items-center gap-3 md:gap-5 rounded-2xl p-3 md:p-4 bg-emerald-500/10 border border-emerald-400/20" data-testid="kiosk-today-item">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <LogIn size={18} className="text-emerald-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-bold text-sm md:text-lg truncate">{p.labour_name || '—'}</div>
                    <PerfDots scores={(perfCache[p.labour_id]?.recent_scores) || []} />
                  </div>
                  <div className="text-[10px] md:text-sm text-white/55 uppercase tracking-wider">
                    {p.punch_time ? new Date(p.punch_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '—'}
                    {p.hours_elapsed != null ? ` · ${p.hours_elapsed}h on site` : ''}
                  </div>
                  {p.subcontractor_name && (
                    <div className="text-[10px] text-white/40 truncate mt-0.5" data-testid={`kiosk-today-sub-${p.labour_id}`}>
                      {p.subcontractor_name}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {summary.present_today > (summary.recent_punches || []).length && (
            <div className="mt-2 text-[10px] md:text-xs text-white/35 text-center" data-testid="kiosk-today-more">
              Showing {(summary.recent_punches || []).length} of {summary.present_today} workers present today
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function KindButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`py-3 rounded-xl flex items-center justify-center gap-2 font-display font-semibold text-sm transition-colors ${
        active ? 'bg-white text-slate-900' : 'bg-white/10 text-white/80 border border-white/15'
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );
}

function ThroughStat({ label, value, testid }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-3 md:p-4 text-center" data-testid={testid}>
      <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
      <div className="font-display font-bold text-lg md:text-2xl tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

// Step 15 — last few daily performance scores as coloured dots (green ≥0.7,
// amber ≥0.4, red <0.4). Shows the worker's recent track record at a glance.
function PerfDots({ scores }) {
  if (!scores || scores.length === 0) return null;
  const last3 = scores.slice(0, 3);
  const color = (s) => (s == null ? 'bg-white/20' : s >= 0.7 ? 'bg-emerald-400' : s >= 0.4 ? 'bg-amber-400' : 'bg-rose-400');
  return (
    <span className="flex items-center gap-1 shrink-0" data-testid="kiosk-perf-dots" title="Last 3 days performance">
      {last3.map((r, i) => (
        <span key={i} className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full ${color(r.performance_score)}`} />
      ))}
    </span>
  );
}

